import * as React from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';

import {
  CaretRightOutlined as PlayArrow,
  DeleteOutlined as Delete,
  EditOutlined as Edit,
  StopOutlined as Stop
} from '@ant-design/icons';
import { Alert as AntdAlert, Menu as AntdMenu } from 'antd';

import Alert from 'components/Alert';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { WebhookStatus, WebhookStatusAlias, WebhookTypeAlias } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

import { useWebhookContext } from '../context';

const Actions = () => {
  const {
    selectedWebhook,
    anchorEl,
    isActionsOpen,
    setIsActionsOpen,
    setIsFormOpen,
    listWebhooks,
    setSelectedWebhook,
    setLoading
  } = useWebhookContext();

  const onFinish = () => {
    setSelectedWebhook(null);
    listWebhooks();
  };

  const onClose = () => {
    setSelectedWebhook(null);
  };

  const handleEdit = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);
    setIsFormOpen(true);
  };

  const handleActive = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);

    const diff = differenceInSeconds(new Date(), new Date(selectedWebhook.updated_at));

    if (diff < 30) {
      Alert.show({
        title: 'Aguarde',
        message: 'Aguarde pelo menos 30s antes de poder ativar/reativar o mesmo webhook.',
        buttonOk: 'Aguardar'
      });
      onFinish();
      return;
    }

    const confirm = await Alert.confirm({
      message: (
        <>
          <p>Deseja realmente ativar esse webhook?</p>
          <p>
            <strong>URL:</strong> {selectedWebhook.url}
          </p>
          <p>
            <strong>Tipo:</strong> {WebhookTypeAlias[selectedWebhook.type as keyof typeof WebhookTypeAlias]}
          </p>
        </>
      ),
      title: 'Atenção, você está ativando um webhook.',
      confirmation: true
    });

    if (confirm) {
      try {
        setLoading(true);
        const response = await webhookService.reactive(selectedWebhook._id);

        if (response.success === true) {
          onFinish();
          toast.success('Webhook ativo com sucesso');
        } else {
          onFinish();
          toast.error('URL do webhook Inválida');
        }
      } catch (e) {
        toast.error(e);
      }
    }

    onClose();
    return;
  };

  const handleInactive = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);

    const diff = differenceInSeconds(new Date(), new Date(selectedWebhook.updated_at));

    if (diff < 30) {
      Alert.show({
        title: 'Aguarde',
        message: 'Aguarde pelo menos 30s antes de poder ativar/reativar o mesmo webhook.',
        buttonOk: 'Aguardar'
      });
      onFinish();
      return;
    }

    const confirm = await Alert.confirm({
      message: (
        <>
          <p>Deseja realmente desativar esse webhook?</p>

          <AntdAlert
            type='error'
            className='my-5 text-sm'
            message='Ao inativar sua url, todos os eventos pendentes e com erro serão arquivados.'
          />

          <p>
            <strong>URL:</strong> {selectedWebhook.url}
          </p>
          <p>
            <strong>Tipo:</strong> {WebhookTypeAlias[selectedWebhook.type as keyof typeof WebhookTypeAlias]}
          </p>
        </>
      ),
      title: 'Atenção, você está desativando um webhook.',
      confirmation: true
    });

    if (confirm) {
      try {
        setLoading(true);
        await webhookService.inactive(selectedWebhook._id);
        onFinish();
        toast.success('Webhook desativado com sucesso');
      } catch (e) {
        toast.error(e);
      }
    }

    onClose();
    return;
  };

  const handleRemove = async (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setIsActionsOpen(false);
    const confirm = await Alert.confirm({
      message: (
        <>
          <p>Deseja realmente remover esse webhook?</p>
          <p>
            <strong>URL:</strong> {selectedWebhook.url}
          </p>
          <p>
            <strong>Tipo:</strong> {WebhookTypeAlias[selectedWebhook.type as keyof typeof WebhookTypeAlias]}
          </p>
          <p>
            <strong>Status:</strong> {WebhookStatusAlias[selectedWebhook.state]}
          </p>
          <p>
            <strong>Atenção: </strong>ao remover o webhook todos os históricos serão removidos.
          </p>
        </>
      ),
      title: 'Atenção, você está removendo um webhook.',
      confirmation: true
    });

    if (confirm) {
      try {
        setLoading(true);
        await webhookService.removeWebhook(selectedWebhook._id);
        toast.success('Webhook removido com sucesso');
        onFinish();
      } catch (e) {
        toast.error(e);
      }
    }

    onClose();
    return;
  };

  if (!selectedWebhook || !anchorEl || !isActionsOpen) return null;

  const rect = anchorEl.getBoundingClientRect();

  const menuItems = [
    { key: 'edit', label: 'Editar', icon: <Edit /> },
    ...(selectedWebhook?.state === WebhookStatus.inactive
      ? [{ key: 'activate', label: 'Ativar', icon: <PlayArrow /> }]
      : []),
    ...(selectedWebhook?.state === WebhookStatus.failing || selectedWebhook?.state === WebhookStatus.active
      ? [{ key: 'deactivate', label: 'Desativar', icon: <Stop /> }]
      : []),
    {
      key: 'remove',
      label: 'Remover',
      icon: <Delete style={{ fontSize: 16, color: '#f44336' }} />,
      danger: true as const
    }
  ];

  const handleMenuClick = ({ key, domEvent }: { key: string; domEvent: React.MouseEvent | React.KeyboardEvent }) => {
    const syntheticEvent = domEvent as unknown as React.SyntheticEvent;
    if (key === 'edit') handleEdit(syntheticEvent);
    if (key === 'activate') handleActive(syntheticEvent);
    if (key === 'deactivate') handleInactive(syntheticEvent);
    if (key === 'remove') handleRemove(syntheticEvent);
  };

  return createPortal(
    <>
      <div className='fixed inset-0 z-1040' onClick={onClose} />
      <div className='fixed z-1050' style={{ top: rect.bottom + 4, left: rect.right - 200 }}>
        <AntdMenu
          items={menuItems}
          onClick={handleMenuClick}
          className='rounded-lg shadow-lg'
          style={{ minWidth: 180 }}
        />
      </div>
    </>,
    document.body
  );
};

export { Actions };
